// Support view: what we sent (or tried to) — newest first, filter by org,
// kind, status. Addresses come back masked. With `orgId` (Admin Panel org
// details) the list is fixed to that org.
import React, {useCallback, useEffect, useState} from 'react';
import {
  Alert, Button, Chip, CircularProgress, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField
} from '@mui/material';

import {searchNotifications, searchOrgNotifications} from '../../actions/notifications';
import {errorMessage, formatTime, searchRequest, PAGE_SIZE, statusColor} from '../../lib/notifications';

const STATUSES = ['', 'queued', 'sent', 'failed', 'bounced', 'delivered'];

const SentEmailsTab = ({kinds = [], orgId}) => {
  const [filters, setFilters] = useState({orgId: orgId || '', kind: '', status: ''});
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (f, p) => {
    setLoading(true);
    setError('');
    try {
      const org = f.orgId.trim();
      const request = searchRequest(f, p);
      const res = await (org ? searchOrgNotifications(org, request) : searchNotifications(request));
      setRows(res.notifications || []);
    } catch (e) {
      setError(errorMessage(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(filters, page); }, [load, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field) => (e) => setFilters({...filters, [field]: e.target.value});
  const search = () => (page === 0 ? load(filters, 0) : setPage(0));

  return (
    <>
      <Stack direction="row" spacing={1} sx={{mb: 2, flexWrap: 'wrap', rowGap: 1}}>
        {!orgId && <TextField size="small" label="Org ID" value={filters.orgId} onChange={set('orgId')} />}
        {kinds.length > 0 && <TextField size="small" select label="Kind" value={filters.kind} onChange={set('kind')} sx={{minWidth: 200}}>
          <MenuItem value="">All</MenuItem>
          {kinds.map((k) => <MenuItem key={k.kind} value={k.kind}>{k.kind}</MenuItem>)}
        </TextField>}
        <TextField size="small" select label="Status" value={filters.status} onChange={set('status')} sx={{minWidth: 140}}>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={search}>Search</Button>
        {loading && <CircularProgress size={24} />}
      </Stack>
      {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Kind</TableCell>
            {!orgId && <TableCell>Org</TableCell>}
            <TableCell>To</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Attempts</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Via</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{formatTime(r.createdAt)}</TableCell>
              <TableCell>{r.kind}</TableCell>
              {!orgId && <TableCell>{r.orgId}</TableCell>}
              <TableCell>{r.recipient}</TableCell>
              <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
              <TableCell>{r.attempts}</TableCell>
              <TableCell>{r.templateVersion ? `v${r.templateVersion}` : ''}</TableCell>
              <TableCell>{r.triggeredVia}</TableCell>
              <TableCell>{r.errorMessage}</TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={orgId ? 8 : 9}>No emails match.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={1} sx={{mt: 1}}>
        <Button disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
        <Button disabled={rows.length < PAGE_SIZE || loading} onClick={() => setPage(page + 1)}>Next</Button>
      </Stack>
    </>
  );
};

export default SentEmailsTab;
